import {
  Badge,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useHeaderOverlay,
} from '@napaicp/ui'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useManagedEvents } from '@/features/events/api'
import { pickLocalized } from '@/features/events/format'
import { useLogout, useSession } from '@/features/session/use-session'
import { platformRoleLabel } from '@/lib/labels'

/**
 * PR-039/UX-030/UX-040 (v3.1 revision, Wave U Phase 1): the top-right
 * Account Menu every signed-in App Shell screen must have. Trigger is
 * `[Avatar] 顯示名稱 ⌄`; the dropdown itself is a pure self-service surface
 * — display name, email, a role badge (never inferred from the account
 * name/email — always the actual `globalRole`), profile-related items, a
 * visual divider, then sign out. "我的競賽" is deliberately **not** listed
 * here any more: PR-048 moved it to the main nav as a high-frequency item,
 * so keeping it here too would duplicate a business action inside what is
 * supposed to be an account-settings surface.
 *
 * P0 student-workspace UX polish (v3.23): "申請主辦" moved out of the main
 * nav (a high-frequency-looking slot every signed-in visitor saw, even
 * though hosting a competition is a rare, deliberate action, not a routine
 * task) into this menu as "申請成為競賽主辦方", between 我的角色 and 登出.
 * Server-side authorization is unchanged — `/competition-requests` already
 * accepted any signed-in visitor before this move; only the entry point's
 * visual weight changed.
 *
 * PR-141/UX-088 (v3.26): Display Name must never be the Header's layout
 * authority. `compact` (used for the always-visible mobile header slot,
 * `root-layout.tsx`'s `mobileActions`) drops the visible name entirely and
 * keeps only the avatar + chevron at a fixed size — a 100-char name can't
 * push the hamburger out of the header. The non-compact desktop trigger
 * instead bounds the name column and ellipses it (BR-109/AC-134/AC-135)
 * rather than growing without limit. The dropdown's open state is routed
 * through `useHeaderOverlay` so it and the mobile navigation Drawer are
 * mutually exclusive (UX-092/BR-110) even though they're two independent,
 * differently-owned Radix primitives.
 */
export function AccountMenu({ compact = false }: { compact?: boolean }) {
  const { t, i18n } = useTranslation(['account', 'common'])
  const navigate = useNavigate()
  const session = useSession()
  const logout = useLogout()
  const { overlay, setOverlay } = useHeaderOverlay()
  // UX-092/BR-110 (v3.26): distinct per-instance key — see `header-overlay.tsx`
  // for why `compact` and non-`compact` cannot share one 'account' value.
  const overlayKey = compact ? 'account-compact' : 'account'

  const displayName =
    session.data?.authenticated === true
      ? session.data.user?.displayName
      : t('common:state.signedIn')
  const email = session.data?.authenticated === true ? session.data.user?.email : undefined
  const globalRole =
    session.data?.authenticated === true ? session.data.user?.globalRole : undefined
  const managed = useManagedEvents(session.data?.authenticated === true)
  const initial = (displayName ?? '?').trim().charAt(0).toUpperCase() || '?'

  function handleSignOut(): void {
    logout.mutate(undefined, {
      onSuccess: () => {
        void navigate('/', { replace: true })
      },
    })
  }

  return (
    <DropdownMenu
      open={overlay === overlayKey}
      onOpenChange={(open) => setOverlay(open ? overlayKey : null)}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'min-w-0 gap-2',
            compact
              ? 'size-11 justify-center px-0'
              : 'max-w-[13.75rem] min-[1440px]:max-w-[16.25rem]',
          )}
        >
          <span className="sr-only">
            {compact ? t('account:menu.trigger') : `${t('account:menu.trigger')}: `}
          </span>
          <span
            aria-hidden="true"
            className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary"
          >
            {initial}
          </span>
          {compact ? null : (
            <span className="min-w-0 flex-1 truncate text-left whitespace-nowrap">
              {displayName}
            </span>
          )}
          <span aria-hidden="true" className="shrink-0 text-muted-foreground">
            ⌄
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate text-sm font-medium">
          {displayName}
        </DropdownMenuLabel>
        {email !== undefined ? (
          <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
            {email}
          </DropdownMenuLabel>
        ) : null}
        {globalRole !== undefined ? (
          <div className="px-2 pb-1.5">
            <Badge variant="neutral">{platformRoleLabel(t, globalRole)}</Badge>
          </div>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account/profile">{t('account:menu.myProfile')}</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/security">{t('account:menu.mySecurity')}</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/roles">{t('account:menu.myRoles')}</Link>
        </DropdownMenuItem>
        {managed.data?.slice(0, 5).map((event) => (
          <DropdownMenuItem key={event.slug} asChild>
            <Link to={`/events/${event.slug}/admin`}>
              {t('account:menu.manageEvent', {
                title: pickLocalized({ zh: event.titleZh, en: event.titleEn }, i18n.language),
              })}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/competition-requests">{t('account:menu.applyToHost')}</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleSignOut} disabled={logout.isPending}>
          {t('account:menu.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
